<template>
    <div class="product-finder">
        <ProgressBar></ProgressBar>

        <QuestionToolTip :html="productFinderStore.pages[productFinderStore.currentStep].tooltip" :icon="false"></QuestionToolTip>

        <form class="product-finder-question-form">
            <fieldset>
                <label class="question">
                    {{ productFinderStore.pages[productFinderStore.currentStep].question }}
                    <QuestionToolTip :html="tooltips[productFinderStore.currentStep]" :resetTooltip="resetTooltip"></QuestionToolTip>
                </label>

                <div v-if="productFinderStore.pages[productFinderStore.currentStep].fields.length" v-for="(field, index) in productFinderStore.pages[productFinderStore.currentStep].fields" :key="index">
                    <RadioInput :class="{ 'is-required' : errors.length }" :default="productFinderStore.selectedOptions[productFinderStore.currentStep]" :data="field" v-if="field.type == 'radio'" @update:modelValue="productFinderStore.setSelectedOption($event); clearErrors();"></RadioInput>
                    <RangeSlider v-else-if="field.type == 'slider'"></RangeSlider>
                </div>

                <div v-else>
                    <RangeSlider></RangeSlider>
                </div>

                <div v-for="(error, index) in errors" :key="index" class="error">
                    <span class="icon-error-warning"></span>
                    <div v-html="error"></div>
                </div>
            </fieldset>

            <div class="inline-buttons">
                <button :class="{'btn btn-grey-hollow': 'button', 'hidden': (productFinderStore.currentStep == 0)}" type="button" @click.prevent="prevStep()">Back to Previous Step</button>
                <button class="btn btn-primary" @click.prevent="nextStep()" :disabled="errors.length > 0" type="submit">Proceed to Next Step</button>
            </div>

            <Spinner v-if="loading == true"></Spinner>

        </form>
    </div>
</template>

<script setup>
    import { ref } from 'vue';
    import RadioInput from "./components/RadioInput.vue";
    import QuestionToolTip from "./components/QuestionToolTip.vue";
    import ProgressBar from './ProgressBar.vue';
    import Spinner from './components/Spinner.vue';

    import { useRouter, useRoute } from 'vue2-helpers/vue-router';
    import { onMounted } from 'vue';
    import { useProductFinderStore } from '../../stores/product-finder';

    const route = useRoute();
    const router = useRouter();

    const productFinderStore = useProductFinderStore();
    productFinderStore.loadStateFromLocalStorage();

    const errors = ref([]);
    const loading = ref(false);
    let tooltipsParsed = JSON.parse(window.tooltips);
    const tooltips = ref(tooltipsParsed || []);
    const resetTooltip = ref(false);

    if (router.firstLoad) {
        productFinderStore.startAgain();
    }

    onMounted(() => {
        productFinderStore.map = window.seatingSolutionFinderMap;

        if (route.name != null && route.name != productFinderStore.pages[productFinderStore.currentStep].routeName) {
            if (productFinderStore.pages[productFinderStore.currentStep].routeName == 'Recommendations') {
                productFinderStore.startAgain();
            } else {
                if (!router.currentRoute.path.includes('gb-ie')) {
                    let path = router.options.routes[productFinderStore.currentStep].path;
                    router.push(path);
                } else {
                    let path = '/gb-ie' + router.options.routes[productFinderStore.currentStep].path;
                    router.push(path);
                }
            }
        }
    });

    const calculateResults = async () => {

        for (let index = 0; index < productFinderStore.map.length; index++) {
            let match = true;
            let productPathSelectedValues = productFinderStore.map[index];
            let products = productPathSelectedValues[6];
            let pathValues = productPathSelectedValues.slice(0, 6);

            if (pathValues[0] == productFinderStore.selectedOptions[0] && pathValues[1] == productFinderStore.selectedOptions[1]) {
                for (let i = 2; i <= 5; i++) {
                    typeof pathValues[i] == 'undefined' ? pathValues[i] = '' : pathValues[i];
                    typeof productFinderStore.selectedOptions[i] == 'undefined' ? productFinderStore.selectedOptions[i] = '' : productFinderStore.selectedOptions[i];
                    let skip = false;

                    if (pathValues[i] == '') {
                        skip = true;
                        console.log('SKIP', '');
                    }

                    if (skip == false && pathValues[i] != productFinderStore.selectedOptions[i]) {
                        match = false;
                    }
                }
            } else {
                match = false;
            }

            if (products && match == true) {
                products.forEach(product => {
                    if (productFinderStore.resultProducts.some(p => p.id === product.id)) {
                        console.debug('Product already added', product);
                    } else {
                        productFinderStore.resultProducts.push(product);
                    }
                });
            }
        }

        return Promise.resolve();

    }

    const nextStep = () => {
        resetTooltip.value = true; // Reset toolTipVisible when moving to the next step
        setTimeout(() => {
            resetTooltip.value = false; // Reset it to default after the update
        }, 0);

        if(productFinderStore.selectedOptions.length > 0 && productFinderStore.selectedOptions[productFinderStore.currentStep] && productFinderStore.pages[productFinderStore.currentStep].required == true) {

            clearErrors();

            if (productFinderStore.currentStep == 5) {
                loading.value = true;
                setTimeout(2000);
                calculateResults();

                setTimeout(() => { 
                    console.debug('Calculating Results'); 
                    loading.value = false;
                    productFinderStore.nextStep();
                }, 1080);

            } else {
                productFinderStore.nextStep();
            }

        } else {

            if(errors.value.length === 0){
                errors.value.push('Please select an option');
            }
        }
    }

    const prevStep = () => {
        const nextStepIndex = productFinderStore.currentIndex - 1;
        productFinderStore.prevStep(nextStepIndex);
    }

    const clearErrors = () => {
        errors.value = [];
    }

</script>

<style lang="scss" scoped>

.hidden {
    visibility: hidden;
}

.error {
    width: 100%;
    height: 40px;
    border-radius: 4px;
    display: flex;
    flex-direction: row;
    justify-content: start;
    align-items: center;

    > div {
        color: #C10707;
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 16px;
    }
}
.icon-error-warning {
    content: url('/build/images/icon-error-warning.svg');
    width: 24px;
    height: 24px;
    margin-right: 8px;
}

.inline-buttons {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-width: 217px;
    margin: 0 auto;

    @media (min-width: 550px) {
        flex-direction: row;
        justify-content: space-between;
        gap: 0;
        max-width: 100%;
        margin: 0 auto;
    }

    .button {
        border-radius: 5px;
        background: #1A2041;
        padding: 20px;
        color: #FFF;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        cursor: pointer;
        width:100%;

        @media (min-width: 550px) {
            width: auto;
        }
    }
}

.product-finder {
    padding-top: 0 !important;
    position: relative;
    background: linear-gradient(0deg, #1A2041 0%, #1A2041 100%), #FFF;

    form {
        position: relative;
        padding: 30px 30px;
        border-radius: 5px;
        background: #FFF;
        box-shadow: 0px 0px 30px 0px rgba(0, 0, 0, 0.05);
        max-width: 750px;
        margin: 0 auto;

        @media (min-width: 1024px) {
            padding: 60px 65px;
        }

        fieldset{
            margin-bottom: 20px;

            @media (min-width: 1024px) {
                margin-bottom: 40px;
            }
        }
    }

    .product-finder-question-form {
        margin-top: 40px;

        @media (min-width: 768px) {
            margin-top: 70px;
        }
    }

    .question{
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 5px;
        position: relative;
        color: #000;
        font-size: 20px;
        line-height: 20px;
        font-style: normal;
        font-weight: 600;
        line-height: 1.4;

        .tooltip {
            display: flex;
        }
        margin-bottom: 35px;

        @media (min-width: 1024px) {
            font-size: 24px;
        }

        div{
            position: relative;
        }
    }
}

</style>